import React, { useCallback, useState } from 'react';

import { Text } from '@atlaskit/primitives/compiled';

import { token } from '@atlaskit/tokens';

import { useIntl } from 'react-intl';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { spotlightMessages } from './messages';

import { EditDashboardImage } from '../assets/EditDashboardImage';

import { type JSX } from 'react/jsx-runtime';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type SpaceDashboardGuideSpotlightProps = {
	canEditDashboard?: boolean;
};

const footerStyles: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	width: '100%',
	gap: token('space.100'),
};

const stepCounterStyles: React.CSSProperties = {
	whiteSpace: 'nowrap',
};

const cardStyles: React.CSSProperties = {
	maxWidth: '280px',
};

export const SpaceDashboardGuideSpotlight = ({
	canEditDashboard = false,
}: SpaceDashboardGuideSpotlightProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { onClosed } = usePostOfficeUserInteractions();
	const [activeStep, setActiveStep] = useState(0);
	const [isDismissed, setIsDismissed] = useState(false);
	const numberOfSteps = canEditDashboard ? 2 : 1;

	const dismiss = useCallback(() => {
		setIsDismissed(true);
		void onClosed({ interactionIdentifier: 'dismissButton' });
	}, [onClosed]);

	const preventOutsideClickDismiss = useCallback(() => {
		/* no-op: users must click Dismiss button */
	}, []);

	const next = useCallback(() => {
		setActiveStep(1);
	}, []);

	const renderStep1 = () => (
		<div style={cardStyles}>
			<SpotlightCard>
				<SpotlightHeader>
					<SpotlightHeadline>{formatMessage(spotlightMessages.step1Title)}</SpotlightHeadline>
				</SpotlightHeader>
				<SpotlightBody>
					<Text as="p">{formatMessage(spotlightMessages.step1Content)}</Text>
				</SpotlightBody>
				<SpotlightFooter>
					{canEditDashboard ? (
						<div style={footerStyles}>
							<span style={stepCounterStyles}>
								<Text as="span" color="color.text.inverse">
									{formatMessage(spotlightMessages.stepCounter, {
										current: 1,
										total: numberOfSteps,
									})}
								</Text>
							</span>
							<SpotlightActions>
								<SpotlightSecondaryAction onClick={dismiss}>
									{formatMessage(spotlightMessages.dismissButton)}
								</SpotlightSecondaryAction>
								<SpotlightPrimaryAction onClick={next}>
									{formatMessage(spotlightMessages.nextButton)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</div>
					) : (
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={dismiss}>
								{formatMessage(spotlightMessages.dismissButton)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					)}
				</SpotlightFooter>
			</SpotlightCard>
		</div>
	);

	const renderStep2 = () => (
		<div style={cardStyles}>
			<SpotlightCard>
				<SpotlightHeader>
					<SpotlightHeadline>{formatMessage(spotlightMessages.step2Title)}</SpotlightHeadline>
				</SpotlightHeader>
				<SpotlightMedia>
					<EditDashboardImage />
				</SpotlightMedia>
				<SpotlightBody>
					<Text as="p">{formatMessage(spotlightMessages.step2Content)}</Text>
				</SpotlightBody>
				<SpotlightFooter>
					<div style={footerStyles}>
						<span style={stepCounterStyles}>
							<Text as="span" color="color.text.inverse">
								{formatMessage(spotlightMessages.stepCounter, {
									current: 2,
									total: numberOfSteps,
								})}
							</Text>
						</span>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={dismiss}>
								{formatMessage(spotlightMessages.dismissButton)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</div>
				</SpotlightFooter>
			</SpotlightCard>
		</div>
	);

	if (isDismissed) {
		return null;
	}

	return (
		<PopoverContent
			placement="right-end"
			isVisible={!isDismissed}
			dismiss={preventOutsideClickDismiss}
		>
			{activeStep === 0 ? renderStep1() : renderStep2()}
		</PopoverContent>
	);
};
